import { RefObject, useRef } from "react";
import styled from "styled-components";
import ScrollFloat from "../../components/ScrollFloat";
import {
  CtaButtonRow,
  CtaGhostButton,
  CtaPrimaryButton,
} from "../../styles/CtaButtons";

const StyledHero = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5rem 3rem 6rem;
  max-width: 110rem;
  background: var(--bg);
  cursor: auto;

  * {
    cursor: inherit;
  }

  a,
  button,
  [role="button"] {
    cursor: pointer;
  }

  @media only screen and (max-width: 600px) {
    padding: 3rem 2rem 4.6rem;
  }
`;

const Title = styled(ScrollFloat)`
  margin: 0 0 2.4rem;
  color: var(--primary);
  font-family: Impact, "Anton", sans-serif;

  .scroll-float-text,
  .word,
  .char {
    font-family: Impact, "Anton", sans-serif;
    font-weight: 800;
    color: var(--primary);
    text-transform: uppercase;
    text-align: center;
    letter-spacing: 0.04em;
    font-size: clamp(3.6rem, 6vw, 6.8rem);
    line-height: 1.08;
  }

  @media only screen and (max-width: 600px) {
    margin: 0 0 1.4rem;
    max-width: 100%;

    .scroll-float-text,
    .word,
    .char {
      font-size: clamp(2.6rem, 9vw, 3.8rem);
      line-height: 1.05;
      letter-spacing: 0.02em;
    }
  }
`;

/* ScrollFloat hardcodes an <h2> for its split-text animation, so the page's
   primary heading is this visually hidden <h1> while the animated heading
   below it is marked decorative. */
const VisuallyHiddenH1 = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* display: contents keeps the animated heading's flex layout untouched. */
const DecorativeTitle = styled.div`
  display: contents;
`;

const Lead = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.65;
  color: var(--main);
  text-align: center;
  max-width: 62rem;
  margin: 0;

  @media only screen and (max-width: 600px) {
    max-width: 34rem;
  }

  a {
    color: var(--primary);
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 3px;
    &:hover {
      color: var(--kusari-green, #4a7a5e);
    }
  }
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 3rem 4.5rem;
  margin-top: 4.5rem;
  width: 100%;
  max-width: 90rem;

  @media only screen and (max-width: 600px) {
    gap: 2rem 2.4rem;
    margin-top: 3.2rem;
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 9rem;
`;

const StatValue = styled.span`
  font-family: "Space Grotesk", sans-serif;
  font-weight: 800;
  font-size: clamp(2.6rem, 4vw, 4.2rem);
  color: var(--primary);
  line-height: 1;
`;

const StatRule = styled.span`
  width: 3.2rem;
  height: 3px;
  background: var(--primary);
  margin-top: 1rem;
  border-radius: 2px;
`;

const StatLabel = styled.span`
  font-family: "Space Grotesk", sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--sub);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 0.8rem;
  text-align: center;
`;

interface HeroProps {
  scrollContainerRef?: RefObject<HTMLElement>;
  showTitle?: boolean;
  showBody?: boolean;
  mobileVariant?: "default" | "titleOnly" | "bodyOnly";
}

const Hero = ({
  scrollContainerRef,
  showTitle = true,
  showBody = true,
  mobileVariant = "default",
}: HeroProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isTitleOnly = mobileVariant === "titleOnly";
  const isBodyOnly = mobileVariant === "bodyOnly";

  return (
    <StyledHero
      id={showTitle && showBody ? "welcome-hero" : undefined}
      ref={sectionRef}
      style={{
        padding:
          isTitleOnly
            ? "0 2rem 1.6rem"
            : isBodyOnly
            ? "1.8rem 2rem 4.2rem"
            : undefined,
      }}
    >
      {showTitle && (
        <>
          <VisuallyHiddenH1>Welcome to Killer GF</VisuallyHiddenH1>
          <DecorativeTitle aria-hidden="true">
            <Title
              scrollContainerRef={scrollContainerRef}
              triggerRef={sectionRef}
              animationDuration={1.4}
              ease="power2.out"
              scrollStart="top 76%"
              scrollEnd="top 42%"
              stagger={0.07}
              scrub={1.1}
            >
              Welcome to Killer GF
            </Title>
          </DecorativeTitle>
        </>
      )}
      {showBody && (
        <>
          <Lead>
            An onchain art collection of 7,777 badass assassins illustrated by former
            Riot Games artist Zeronis. Released by the{" "}
            <a href="https://kusari.org" target="_blank" rel="noopener noreferrer">
              Kusari
            </a>{" "}
            team in January 2022.
          </Lead>
          <CtaButtonRow $center>
            <CtaPrimaryButton
              type="button"
              onClick={() =>
                window.open("https://opensea.io/collection/killergf", "_blank")
              }
            >
              View on OpenSea
            </CtaPrimaryButton>
            <CtaGhostButton
              type="button"
              onClick={() =>
                window.open("https://discord.gg/cKWpT7HGam", "_blank")
              }
            >
              Join our Discord
            </CtaGhostButton>
          </CtaButtonRow>
          <StatsRow>
            <Stat>
              <StatValue>3.5k+</StatValue>
              <StatRule />
              <StatLabel>NFT Holders</StatLabel>
            </Stat>
            <Stat>
              <StatValue>48+</StatValue>
              <StatRule />
              <StatLabel>Monthly art airdrops</StatLabel>
            </Stat>
            <Stat>
              <StatValue>$100k+</StatValue>
              <StatRule />
              <StatLabel>Charity raised</StatLabel>
            </Stat>
            <Stat>
              <StatValue>8k+</StatValue>
              <StatRule />
              <StatLabel>Community members</StatLabel>
            </Stat>
          </StatsRow>
        </>
      )}
    </StyledHero>
  );
};

export default Hero;
